import { ProductHits } from "./ProductHits";
import { ProductListStyles, ProductListWrapperStyles } from "./ProductListStyles";
import { Grid } from "@ecco/ui/grid";
import { Flex } from "@ecco/ui/flex";
import type { FC, ReactElement, JSX } from "react";
import { useMemo, useRef } from "react";
import { isEmpty } from "lodash-es";
import { useStore } from "@store/index";
import type { IPartStoryTile } from "@contentful-api/types/contentful";
import { resolveMediaAsset } from "@lib/contentful/api/helpers/resolveMediaAsset";
import { EditorialWidget } from "./EditorialWidget";
import { useCurrentRefinements, useInstantSearch } from "react-instantsearch";
import { resolveWidgetPosition } from "./widget-positions";
import { getStoryTileLink, isContentCardWidget } from "./editorialWidgetHelpers";
import { useSmBreakpoint } from "@ecco/ui/use-breakpoint";

interface IProductHitsWrapperProps {
    productListHits: any[];
    footerContent: JSX.Element;
    noResultsContent: string | ReactElement<any>;
    shouldSaveLastVisitedPath?: boolean;
    productListName: string;
    widgetSize?: string;
    editorialWidgets?: IPartStoryTile[];
}

export const ProductHitsWrapper: FC<IProductHitsWrapperProps> = ({
    productListHits,
    footerContent,
    noResultsContent,
    shouldSaveLastVisitedPath = false,
    productListName,
    widgetSize,
    editorialWidgets,
}) => {
    const ref = useRef(null);
    // We will show widgets only when filters are not applied to avoid re-calculations on the layouts.
    const { items: filtersAreApplied } = useCurrentRefinements();
    // We don't want the debounce, otherwise will casue layout shift
    const sm = useSmBreakpoint();

    const lastVisitedPlpContainerHeight = useStore(
        (state) => state.data.products?.lastVisitedPlpContainerHeight
    );
    const lastVisitedPlp = useStore((state) => state.data.products?.lastVisitedPlp);
    const lastLoadedPage = useStore((state) => state.data.products?.lastLoadedPage);

    const computeWidgetPosition = useMemo(
        () => (products: number, index: number, editorialWidget: IPartStoryTile) => {
            const isContentCard = isContentCardWidget(editorialWidget);
            const position = resolveWidgetPosition(products, index, widgetSize, sm, isContentCard);

            return position;
        },
        [widgetSize, sm]
    );

    const { results } = useInstantSearch();

    return !isEmpty(productListHits) ? (
        <Flex
            sx={{ ...ProductListWrapperStyles }}
            data-testid="product-list-grid"
            data-no-product-hits={!results.__isArtificial}
        >
            <Grid
                sx={ProductListStyles(isEmpty(editorialWidgets?.length))}
                ref={ref}
                minH={
                    lastVisitedPlp &&
                    window?.location?.pathname + window.location.search === lastVisitedPlp
                        ? `${lastVisitedPlpContainerHeight}px`
                        : "auto"
                }
            >
                <ProductHits
                    productListHits={productListHits}
                    shouldSaveLastVisitedPath={shouldSaveLastVisitedPath}
                    productListName={productListName}
                    containerHeight={lastLoadedPage > 1 ? ref?.current?.clientHeight : undefined}
                />
                {!!editorialWidgets &&
                    editorialWidgets.map((editorialWidget, index) => {
                        const isContentCard = isContentCardWidget(editorialWidget);
                        const resolvedAsset = resolveMediaAsset(
                            editorialWidget.fields.asset,
                            editorialWidget.fields.image
                        );
                        return (
                            resolvedAsset && (
                                <EditorialWidget
                                    image={editorialWidget.fields.image}
                                    asset={resolvedAsset}
                                    title={editorialWidget.fields.title}
                                    description={editorialWidget.fields.description}
                                    styles={computeWidgetPosition(
                                        productListHits.length,
                                        index,
                                        editorialWidget
                                    )}
                                    link={getStoryTileLink(editorialWidget)}
                                    widgetSize={widgetSize}
                                    isVisible={isEmpty(filtersAreApplied)}
                                    isContentCard={isContentCard}
                                    key={editorialWidget.sys.id}
                                />
                            )
                        );
                    })}
            </Grid>
            {productListHits?.length > 0 && footerContent}
        </Flex>
    ) : (
        <Flex
            direction="column"
            w="100%"
            minW={0}
            fontWeight="bold"
            px={[3, 3, 6, 6]}
            py={[10, 10, 16, 16]}
            data-testid="productHits"
            data-no-product-hits={!results.__isArtificial}
        >
            {results.__isArtificial ? "" : noResultsContent}
        </Flex>
    );
};
